import * as React from "react";
import {useEffect} from "react";
import {listModuleInstances, listProjects} from "../BackendFacade/apiCalls";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {IModule} from "../../../BackendFacade/IModuleInstances";
import {IProject} from "../Interfaces/IAllProjects";
import {IIdAnswer} from "../Interfaces/IIdFinder";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import {odxIconSets} from "../../../OdxComponents/constants";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import OdxInput from "../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import OdxIcon from "../../../OdxComponents/NativeOdx/Odx-Icon";
import OdxTable from "../../../OdxComponents/NativeOdx/Table/Odx-Table";
import {OdxTableRow} from "../../../OdxComponents/NativeOdx/Table/Odx-TableRow";
import {OdxTableCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Tablecell";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import {simplifyForHtmlId} from "../../../Utils/transformer";
import OdxContentBox from "../../../OdxComponents/NativeOdx/Odx-Contentbox";

const LISTOFPROJECTS = "listOfProjects"

const IdFinder: React.FC = () => {
    const [listOfProjects, setListOfProjects] = React.useState<IProject[]>();
    const [listOfModules, setListOfModules] = React.useState<IModule[]>();
    const [search, setSearch] = React.useState<string>("");

    const {createErrorToast, setLoading} = useMain();
    const baseId = 'hub-id-finder';

    useModuleViewTracking('hub-idfinder')

    useEffect(() => {
        // Define an async function inside the useEffect
        const fetchData = async () => {
            setLoading(true);
            let allProjects;

            if (!GlobalDictionary.get(LISTOFPROJECTS)) {
                allProjects = await getListOfAllProjects();
                if (allProjects.length === 0) return;
                // Assuming you want to cache the projects in the global dictionary
                GlobalDictionary.set(LISTOFPROJECTS, allProjects);
            } else {
                allProjects = GlobalDictionary.get(LISTOFPROJECTS);
            }
            setListOfProjects(allProjects);

            // get all modules to all projects
            await gatherAllUsedModules(allProjects);
            setLoading(false);
        };

        // Call the async function
        fetchData();
    }, []);

    const getListOfAllProjects = async () => {
        return listProjects().then((data) => {
            if (data.success) {
                GlobalDictionary.set(LISTOFPROJECTS, data.data.value)
                return data.data.value as IProject[]
            } else {
                createErrorToast("Error", data.message);
                return [] as IProject[]
            }
        })
    }

    /**
     * Get all modules to a project
     * @param projectId
     */
    const getProjectModules = async (projectId: string) => {
        return listModuleInstances(projectId).then((data) => {
            if (data.success) {
                const ret = data.data.value as IModule[]
                // set the project id of every module
                ret.forEach((module) => {
                    module.projectId = projectId
                })
                return ret
            } else {
                createErrorToast("Error", data.message);
            }
        })
    }

    const translateProjectIdToProjectName = (projectId: string) => {
        const projectList = GlobalDictionary.get(LISTOFPROJECTS) as IProject[]
        if (!projectList) return ""
        const project = projectList.find((i) => i.projectId === projectId)
        if (!project) return ""
        return project.displayName
    }

    /**
     * Sort all modules to a Table of modules and their usage
     * @param allProjects
     */
    const gatherAllUsedModules = async (allProjects: IProject[]) => {
        const allModules: IModule[] = []
        const listToLoad = [] as Promise<void>[]
        allProjects.forEach((project) => {
            listToLoad.push(
                getProjectModules(project.projectId).then((modules) => {
                    allModules.push(...modules)
                })
            )
        })
        await Promise.all(listToLoad)
        setListOfModules(allModules)

    }

    const searchForId = (id: string): IIdAnswer[] => {
        if (!listOfProjects || !listOfModules) return []
        if (id === "") return []
        // search in project for id
        const answer: IIdAnswer[] = []
        listOfProjects.filter((project) => project.projectId.includes(id)).forEach((project) => {
            answer.push({
                id: project.projectId,
                projectId: project.projectId,
                projectName: project.displayName,
                moduleName: "",
                moduleId: "",
                linkedId: "",
            })
        })
        // search in modules for id
        listOfModules.filter((module) => module.moduleInstanceId.includes(id)).forEach((module) => {
            answer.push({
                id: module.moduleInstanceId,
                projectId: module.projectId,
                projectName: translateProjectIdToProjectName(module.projectId),
                moduleName: module.displayName,
                moduleId: module.moduleInstanceId,
                linkedId: "",
            })
        })
        // search in linked modules for id
        listOfModules.filter((module) => module.linkedId.projectId.includes(id)).forEach((module) => {
            answer.push({
                id: module.linkedId.projectId,
                projectId: module.projectId,
                projectName: translateProjectIdToProjectName(module.projectId),
                moduleName: module.displayName,
                moduleId: module.moduleInstanceId,
                linkedId: module.linkedId.projectId,
            })
        })
        return answer
    }

    const matchedBy = (answer: IIdAnswer) => {
        if (answer.projectId === answer.id) return "Project id"
        if (answer.moduleId === answer.id) return "Module id"
        if (answer.linkedId === answer.id) return "Linked id"
        return ""
    }

    const SearchField = <OdxInput isRequired={false}
                                  id={`${baseId}-search-input`}
                                  label={""}
                                  error={undefined}
                                  value={search}
                                  onChange={(e) => setSearch(e.target.value)}
                                  odxIcon={<OdxIcon iconSet={odxIconSets.core} size={"sm"} name={"search"}/>}
                                  justifyIcon={"start"}
                                  placeholder={"Search for Id"}
    />

    return <div id={`${baseId}-view`}>
        <OdxAreaHeader text={"ID Finder"}
                       subTitle={<div id={`${baseId}-subtitle`}>Search for any project id, module instance id and linked id.</div>}
                       odxSize={"large"}
                       id={`${baseId}-header`}
        />
        <OdxContentBox id={`${baseId}-content`}>
            <div id={`${baseId}-results`}>
                <div id={`${baseId}-search`}>{SearchField}</div>
                <OdxTable className={"pt-10 table-fixed"}
                          variant={"striped"}
                          id={`${baseId}-table`}
                          headerData={
                              [
                                  {name: "Matched By", title: "Matched By", sortable: false},
                                  {name: "Project name", title: "Project name", sortable: true},
                                  {name: "Module name", title: "Module name", sortable: true},
                                  {name: "Project id", title: "Project id", sortable: false},
                                  {name: "Module id", title: "Module id", sortable: false},
                                  {name: "Linked id", title: "Linked id", sortable: false},
                              ]
                          }
                >
                    {searchForId(search).map((answer, index) => {
                        const answerKey = simplifyForHtmlId(answer.id || `answer-${index}`);
                        return <OdxTableRow key={answer.id ?? String(index)} id={`${baseId}-row-${answerKey}`}>
                            <OdxTableCell style={{width: "120px"}} id={`${baseId}-cell-${answerKey}-matched`}>
                                {matchedBy(answer)}
                            </OdxTableCell>
                            <OdxTableCell id={`${baseId}-cell-${answerKey}-project-name`}>{answer.projectName}</OdxTableCell>
                            <OdxTableCell id={`${baseId}-cell-${answerKey}-module-name`}>{answer.moduleName}</OdxTableCell>
                            <OdxTableCell id={`${baseId}-cell-${answerKey}-project-id`}>{answer.projectId}</OdxTableCell>
                            <OdxTableCell id={`${baseId}-cell-${answerKey}-module-id`}>{answer.moduleId}</OdxTableCell>
                            <OdxTableCell id={`${baseId}-cell-${answerKey}-linked-id`}>{answer.linkedId}</OdxTableCell>
                        </OdxTableRow>
                    })}
                </OdxTable>
            </div>
        </OdxContentBox>
    </div>
}

export default IdFinder;
